"use client";

import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { DateRange } from "react-day-picker";

import { Button } from "@/core/components/ui/button";
import { Calendar } from "@/core/components/ui/calendar"; // shadcn (react-day-picker)
import { Input } from "@/core/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { cn } from "@/core/utils/custom-styles";

import {
  EVENT_CATEGORIES,
  EVENTS,
  useActionsTrack,
} from "@/_old/features/analytics";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import {
  addDays,
  endOfMonthLocal,
  startOfMonthLocal,
  todayLocal,
  toYMDLocal,
} from "@/dashboard/utils/date-params";
// presets anuais (ex: "este ano") foram removidos; mantemos apenas os intervalos necessários

// ------ helpers de formatação/parse no padrão BR ------
function fmtBR(d?: Date | null) {
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function parseBR(input: string): Date | null {
  // aceita "DD/MM/AAAA" (com ou sem zeros à esquerda)
  const m = input.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  const d = new Date(yyyy, mm - 1, dd);
  // validação simples
  return d.getFullYear() === yyyy &&
    d.getMonth() === mm - 1 &&
    d.getDate() === dd
    ? d
    : null;
}

export type DateQuickFiltersPreset =
  | "today"
  | "yesterday"
  | "this_month"
  | "custom";

type Preset = DateQuickFiltersPreset;

export function DateQuickFilters({
  event,
  eventCategory,
  className,
  onSubmit,
  onChange,
  standalone = false,
  hideDateLabel = false,
  defaultPreset = "this_month",
  buttonOutlineClassName,
}: {
  event?: keyof typeof EVENTS;
  eventCategory?: keyof typeof EVENT_CATEGORIES;
  className?: string;
  onSubmit?: boolean;
  onChange?: (
    value: { startDate: string; endDate: string; datePreset: Preset } | null,
  ) => void;
  /** Quando true, não lê nem grava o store do painel; use com `onChange` para controlar o intervalo na tela atual. */
  standalone?: boolean;
  hideDateLabel?: boolean;
  defaultPreset?: Preset;
  buttonOutlineClassName?: string;
}) {
  const { filters, setDateRange } = useDashboardFilters();
  const [active, setActive] = React.useState<Preset>(
    filters.datePreset as Preset,
  );
  const { onActionClick } = useActionsTrack();
  // popover state
  const [open, setOpen] = React.useState(false);

  // estado local da seleção custom
  const [range, setRange] = React.useState<DateRange | undefined>(undefined);
  const [fromText, setFromText] = React.useState<string>("");
  const [toText, setToText] = React.useState<string>("");

  const [lastCustomYMD, setLastCustomYMD] = React.useState<{
    startDate: string;
    endDate: string;
  } | null>(null);

  const pushDateRange = React.useCallback(
    (startYMD: string, endYMD: string, preset: Preset) => {
      if (standalone || onSubmit) {
        onChange?.({
          startDate: startYMD,
          endDate: endYMD,
          datePreset: preset,
        });
      } else {
        setDateRange(startYMD, endYMD);
      }
    },
    [standalone, onSubmit, onChange, setDateRange],
  );

  // inicializa com "Hoje" se não houver datas no store (modo painel)
  React.useEffect(() => {
    if (standalone) return;
    if (!filters.startDate || !filters.endDate) {
      if (filters.datePreset === "today") {
        const t = todayLocal();
        const ymd = toYMDLocal(t);
        if (onSubmit) {
          onChange?.({
            startDate: ymd,
            endDate: ymd,
            datePreset: filters.datePreset as Preset,
          });
        } else {
          setDateRange(ymd, ymd);
        }
        setRange({ from: t, to: t });
        setFromText(fmtBR(t));
        setToText(fmtBR(t));
      } else if (filters.datePreset === "this_month") {
        const now = todayLocal();
        const s = startOfMonthLocal(now);
        const e = endOfMonthLocal(now);
        if (onSubmit) {
          onChange?.({
            startDate: toYMDLocal(s),
            endDate: toYMDLocal(e),
            datePreset: filters.datePreset as Preset,
          });
        } else {
          setDateRange(toYMDLocal(s), toYMDLocal(e));
        }
        setRange({ from: s, to: e });
        setFromText(fmtBR(s));
        setToText(fmtBR(e));
      }
      setActive(filters.datePreset as Preset);
    } else if (!range?.from || !range?.to) {
      // sincroniza UI local com o store quando chega preenchido
      const [ys, ms, ds] = filters.startDate.split("-").map(Number);
      const [ye, me, de] = filters.endDate.split("-").map(Number);
      const s = new Date(ys, ms - 1, ds);
      const e = new Date(ye, me - 1, de);
      setRange({ from: s, to: e });
      setFromText(fmtBR(s));
      setToText(fmtBR(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate, standalone]);

  const standaloneInitRef = React.useRef(false);
  React.useEffect(() => {
    if (!standalone || standaloneInitRef.current) return;
    standaloneInitRef.current = true;
    const now = todayLocal();
    if (defaultPreset === "today") {
      const ymd = toYMDLocal(now);
      pushDateRange(ymd, ymd, "today");
      setActive("today");
      setRange({ from: now, to: now });
      setFromText(fmtBR(now));
      setToText(fmtBR(now));
      return;
    }
    if (defaultPreset === "yesterday") {
      const y = addDays(now, -1);
      const ymd = toYMDLocal(y);
      pushDateRange(ymd, ymd, "yesterday");
      setActive("yesterday");
      setRange({ from: y, to: y });
      setFromText(fmtBR(y));
      setToText(fmtBR(y));
      return;
    }
    if (defaultPreset === "this_month") {
      const s = startOfMonthLocal(now);
      const e = endOfMonthLocal(now);
      pushDateRange(toYMDLocal(s), toYMDLocal(e), "this_month");
      setActive("this_month");
      setRange({ from: s, to: e });
      setFromText(fmtBR(s));
      setToText(fmtBR(e));
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standalone]);

  // presets
  const applyPreset = React.useCallback(
    (preset: Preset) => {
      if (event) {
        onActionClick(event, eventCategory as keyof typeof EVENT_CATEGORIES);
      }

      const now = todayLocal();

      if (preset === "today") {
        const ymd = toYMDLocal(now);
        pushDateRange(ymd, ymd, "today");
        setActive("today");
        setRange({ from: now, to: now });
        setFromText(fmtBR(now));
        setToText(fmtBR(now));
        setOpen(false);
        return;
      }

      if (preset === "yesterday") {
        const y = addDays(now, -1);
        const ymd = toYMDLocal(y);
        pushDateRange(ymd, ymd, "yesterday");
        setActive("yesterday");
        setRange({ from: y, to: y });
        setFromText(fmtBR(y));
        setToText(fmtBR(y));
        setOpen(false);
        return;
      }

      if (preset === "this_month") {
        const s = startOfMonthLocal(now);
        const e = endOfMonthLocal(now);
        pushDateRange(toYMDLocal(s), toYMDLocal(e), "this_month");
        setActive("this_month");
        setRange({ from: s, to: e });
        setFromText(fmtBR(s));
        setToText(fmtBR(e));
        setOpen(false);
        return;
      }

      // abre popover do custom
      setActive("custom");
      setOpen(true);
    },
    [pushDateRange, event, eventCategory, onActionClick],
  );

  // quando abre o custom, garante que inputs exibam o valor atual
  const onOpenChange = (o: boolean) => {
    if (o && range?.from && range?.to) {
      setFromText(fmtBR(range.from));
      setToText(fmtBR(range.to));
    }
    setOpen(o);
  };

  // digitação nos inputs -> atualiza state e (se válido) o range
  const handleFromChange = (v: string) => {
    setFromText(v);
    const d = parseBR(v);
    if (d) {
      setRange((old) => ({ from: d, to: old?.to ?? d }));
    }
  };
  const handleToChange = (v: string) => {
    setToText(v);
    const d = parseBR(v);
    if (d) {
      setRange((old) => ({ from: old?.from ?? d, to: d }));
    }
  };

  // seleção no calendário -> reflete nos inputs
  const handleCalendarSelect = (r?: DateRange) => {
    setRange(r);
    setFromText(r?.from ? fmtBR(r.from) : "");
    setToText(r?.to ? fmtBR(r.to) : "");
  };

  // aplicar custom
  const canApply = Boolean(range?.from && range?.to);
  const applyCustom = () => {
    if (!canApply) return;
    if (event) {
      onActionClick(event, eventCategory as keyof typeof EVENT_CATEGORIES);
    }
    const s = toYMDLocal(range!.from!);
    const e = toYMDLocal(range!.to!);
    pushDateRange(s, e, "custom");
    setLastCustomYMD({ startDate: s, endDate: e });
    setOpen(false);
  };

  // label reduzido do botão custom quando ativo
  const customLabel = React.useMemo(() => {
    if (active !== "custom") return "Outra data";
    if (standalone && lastCustomYMD) {
      const [ys, ms, ds] = lastCustomYMD.startDate.split("-").map(Number);
      const [ye, me, de] = lastCustomYMD.endDate.split("-").map(Number);
      const s = new Date(ys, ms - 1, ds);
      const e = new Date(ye, me - 1, de);
      return `${fmtBR(s)} → ${fmtBR(e)}`;
    }
    if (!standalone && filters.startDate && filters.endDate) {
      const [ys, ms, ds] = filters.startDate.split("-").map(Number);
      const [ye, me, de] = filters.endDate.split("-").map(Number);
      const s = new Date(ys, ms - 1, ds);
      const e = new Date(ye, me - 1, de);
      return `${fmtBR(s)} → ${fmtBR(e)}`;
    }
    return "Outra data";
  }, [active, standalone, lastCustomYMD, filters.startDate, filters.endDate]);

  React.useEffect(() => {
    if (standalone) return;
    if (filters.datePreset) {
      setActive(filters.datePreset as Preset);
    }
  }, [filters.datePreset, standalone]);

  return (
    <div className={cn("space-y-2", className)}>
      {!hideDateLabel && (
        <div className="text-sm font-medium text-neutral-800">Data</div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant={active === "today" ? "black" : "outline"}
          className={cn(active !== "today" && buttonOutlineClassName)}
          onClick={() => applyPreset("today")}
        >
          Hoje
        </Button>

        <Button
          variant={active === "yesterday" ? "black" : "outline"}
          className={cn(active !== "yesterday" && buttonOutlineClassName)}
          onClick={() => applyPreset("yesterday")}
        >
          Ontem
        </Button>

        <Button
          variant={active === "this_month" ? "black" : "outline"}
          className={cn(active !== "this_month" && buttonOutlineClassName)}
          onClick={() => applyPreset("this_month")}
        >
          Este mês
        </Button>

        {/* presets adicionais removidos; só "custom" continua via popover */}

        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant={active === "custom" ? "black" : "outline"}
              className={cn(
                "flex gap-2 items-center",
                active !== "custom" && buttonOutlineClassName,
              )}
              onClick={() => applyPreset("custom")}
            >
              <CalendarIcon className="w-4 h-4" />
              {customLabel}
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-[320px] p-3" align="start">
            <div className="flex flex-col gap-3">
              {/* inputs de data */}
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={fromText}
                  onChange={(e) => handleFromChange(e.target.value)}
                  placeholder="DD/MM/AAAA"
                  inputMode="numeric"
                />
                <Input
                  value={toText}
                  onChange={(e) => handleToChange(e.target.value)}
                  placeholder="DD/MM/AAAA"
                  inputMode="numeric"
                />
              </div>

              {/* calendário range */}
              <Calendar
                mode="range"
                selected={range}
                onSelect={handleCalendarSelect}
                numberOfMonths={1}
                defaultMonth={range?.from ?? todayLocal()}
                locale={ptBR}
                className="rounded-md border"
              />

              {/* ação principal */}
              <Button
                onClick={applyCustom}
                disabled={!canApply}
                className="bg-[#FFD900] text-black hover:brightness-95"
              >
                Filtrar
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
